var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { styled, css } from '../theme-provider';
import { Text, TextTag, TokenTextColor } from '../text';
import { getCommonProps } from '../helpers/common-props';
import { createA11yOutlineStyles } from '../helpers/a11y';
const StyledIndicator = styled.span.withConfig({ displayName: "StyledIndicator", componentId: "sc-ejzpbb" }) `position:relative;display:block;width:${({ theme }) => theme.size.static530};height:${({ theme }) => theme.size.static350};background-color:${({ theme, disabled }) => theme.colors.controllers.on[disabled ? 'disabled' : 'default']};transition:${({ theme }) => theme.animation.duration.statusIndicator};border-radius:${({ theme }) => theme.size.static350};border:${({ theme }) => theme.size.border.heavy} solid ${({ theme, disabled }) => theme.colors.controllers.on[disabled ? 'disabled' : 'default']};&::before{content:'';position:absolute;${({ position, theme }) => {
    const startTop = parseInt(theme.size.static350, 10) / 2 -
        parseInt(theme.size.border.heavy, 10) -
        parseInt(theme.size.static250, 10) / 2;
    const offset = (theme.direction === 'rtl' ? -1 : 1) *
        (parseInt(theme.size.static530, 10) -
            parseInt(theme.size.static350, 10));
    return css `
				top: ${startTop}px;
				inset-inline-start: ${startTop}px;
				${position === 'end' && `transform translateX(${offset}px);`}
			`;
}} height:${({ theme }) => theme.size.static250};width:${({ theme }) => theme.size.static250};background-color:${({ theme }) => theme.colors.background.primary};border-radius:50%;transition:${({ theme }) => theme.animation.duration.statusIndicator};@media (prefers-reduced-motion){transition:none;}}`;
const StyledInput = styled.input.withConfig({ displayName: "StyledInput", componentId: "sc-efqwx8" }) `position:absolute;opacity:0;outline:none;user-select:none;height:100%;inset-inline-start:0;`;
const StyledLabel = styled.label.withConfig({ displayName: "StyledLabel", componentId: "sc-1c1wvjn" }) `position:relative;cursor:pointer;color:${({ theme, disabled }) => theme.colors.controllers.off[disabled ? 'disabled' : 'default']};transition:${(props) => props.theme.animation.duration.statusIndicator};`;
const StyledWrapper = styled.button.withConfig({ displayName: "StyledWrapper", componentId: "sc-cldjju" }) `display:inline-grid;grid-column-gap:${(props) => props.theme.size.static200};grid-template-columns:auto auto auto;align-items:center;position:relative;border:none;background:none;cursor:pointer;isolation:isolate;${(props) => !props.disabled &&
    css `
			&:hover:not(:focus-within) {
				${StyledLabel} {
					color: ${({ theme }) => theme.colors.controllers.on.hover};
				}

				${StyledIndicator} {
					border: ${({ theme }) => theme.size.border.heavy} solid
						${({ theme }) => theme.colors.controllers.on.hover};

					background-color: ${({ theme }) => theme.colors.controllers.on.hover};

					&::before {
						background-color: ${props.theme.colors.background.primary};
					}
				}
			}
		`} ${(props) => props.disabled &&
    `
		cursor: not-allowed;
		& ${StyledLabel} {
			cursor: not-allowed;
		}
	`} &:focus-visible,&:focus-within,&:focus{outline:${({ theme }) => createA11yOutlineStyles(theme)};}`;
export const Toggle = (props) => {
    const { options, value, defaultValue, name, onChange, disabled = false, 'aria-describedby': ariaDescribedBy } = props, rest = __rest(props, ["options", "value", "defaultValue", "name", "onChange", "disabled", 'aria-describedby']);
    const commonProps = getCommonProps(props);
    const [currentValue, setCurrentValue] = React.useState(value !== null && value !== void 0 ? value : defaultValue);
    const isControlled = value != null;
    const value1 = options[0].value;
    const value2 = options[1].value;
    const val1Checked = (value !== null && value !== void 0 ? value : currentValue) === value1;
    const val2Checked = (value !== null && value !== void 0 ? value : currentValue) === value2;
    const onToggleClick = React.useCallback(() => {
        onChange === null || onChange === void 0 ? void 0 : onChange(val1Checked ? value2 : value1);
        if (!isControlled) {
            if (val1Checked) {
                setCurrentValue(value2);
            }
            else {
                setCurrentValue(value1);
            }
        }
    }, [onChange, isControlled, val1Checked, value1, value2]);
    return (React.createElement(StyledWrapper, Object.assign({ role: "button", tabIndex: 0, onClick: onToggleClick, disabled: disabled, "aria-label": `${val1Checked ? options[0].value : options[1].value} selected`, "aria-live": "polite" }, commonProps, rest),
        React.createElement(StyledLabel, { disabled: disabled },
            React.createElement(StyledInput, { name: name, type: "radio", value: options[0].value, checked: val1Checked, disabled: disabled, readOnly: true, "aria-describedby": ariaDescribedBy, tabIndex: -1 }),
            React.createElement(Text, { tag: TextTag.span, color: TokenTextColor.inherit }, options[0].label)),
        React.createElement(StyledIndicator, { "aria-hidden": true, disabled: disabled, position: val1Checked ? 'start' : 'end' }),
        React.createElement(StyledLabel, { disabled: disabled },
            React.createElement(StyledInput, { name: name, type: "radio", value: options[1].value, checked: val2Checked, disabled: disabled, readOnly: true, "aria-describedby": ariaDescribedBy, tabIndex: -1 }),
            React.createElement(Text, { tag: TextTag.span, color: TokenTextColor.inherit }, options[1].label))));
};
